import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import collabApi from "api/collaboration";
import { handleStatusChanged } from "actions/collaboration";
import { updatePostFormField } from "actions/postForm";
import { FIELDS } from "constants/PostFormFields";
import { User } from "features/user";
import { incrementCollaborationUnreadInCache } from "./collaboration-queries";

interface GeneralChannelSubscription {
  count: number;
  dispose?: () => void;
  pending?: Promise<(() => void) | undefined>;
}

const generalChannelSubscriptions = new Map<
  string,
  GeneralChannelSubscription
>();

export function useCollaborationGeneralChannel(user?: User | null) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.adminId || !user?.userId) return;

    const subscriptionKey = `${user.adminId}:${user.userId}`;
    const existingSubscription =
      generalChannelSubscriptions.get(subscriptionKey);

    if (existingSubscription) {
      existingSubscription.count += 1;
    } else {
      const subscription: GeneralChannelSubscription = { count: 1 };
      generalChannelSubscriptions.set(subscriptionKey, subscription);
      let pending: Promise<(() => void) | undefined>;
      pending = collabApi
        .subscribeToGeneralChannelCallbacks(user.adminId, {
          onUnread: ({ channelId, createdBy }) => {
            if (createdBy !== user.userId) {
              incrementCollaborationUnreadInCache(queryClient, channelId);
            }
          },
          onStatusChanged: ({ post, postType }) => {
            dispatch(handleStatusChanged(postType, post));
            dispatch(
              updatePostFormField(post.postId, {
                [FIELDS.POST_STATUS]: post.newStatus.key,
              })
            );
          },
        })
        .then((unsubscribe) => {
          const currentSubscription =
            generalChannelSubscriptions.get(subscriptionKey);
          if (!currentSubscription) {
            unsubscribe();
            return unsubscribe;
          }
          currentSubscription.dispose = unsubscribe;
          currentSubscription.pending = undefined;
          return unsubscribe;
        })
        .catch(() => {
          const currentSubscription =
            generalChannelSubscriptions.get(subscriptionKey);

          if (currentSubscription?.pending === pending) {
            currentSubscription.pending = undefined;
          }

          return undefined;
        });
      subscription.pending = pending;
    }

    return () => {
      const currentSubscription =
        generalChannelSubscriptions.get(subscriptionKey);
      if (!currentSubscription) return;
      currentSubscription.count -= 1;
      if (currentSubscription.count > 0) return;
      generalChannelSubscriptions.delete(subscriptionKey);
      if (currentSubscription.dispose) {
        currentSubscription.dispose();
        return;
      }
      currentSubscription.pending?.then((unsubscribe) => unsubscribe?.());
    };
  }, [dispatch, queryClient, user?.adminId, user?.userId]);
}
